import { Search } from "lucide-react";
import { useMemo, useState } from "react";

export function EmptyState({ title = "Belum ada data", desc, action, icon: Icon }) {
  return (
    <div className="text-center py-14">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center">
        {Icon ? <Icon className="w-6 h-6" /> : <Search className="w-6 h-6" />}
      </div>
      <div className="mt-4 font-cabinet text-lg font-bold text-slate-800">{title}</div>
      {desc && <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export default function DataTable({
  columns,
  rows,
  keyField = "id",
  searchable = true,
  searchFields = [],
  actions,
  empty,
  toolbar,
  onRowClick,
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q || !searchable) return rows;
    const needle = q.toLowerCase();
    const fields = searchFields.length ? searchFields : columns.filter((c) => c.searchable !== false).map((c) => c.key);
    return rows.filter((r) => fields.some((f) => String(r[f] ?? "").toLowerCase().includes(needle)));
  }, [q, rows, searchable, searchFields, columns]);

  return (
    <div className="gc-card p-0 overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          {searchable && (
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari…" className="gc-input pl-9" data-testid="table-search" />
            </div>
          )}
          {toolbar}
        </div>
        {actions}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, idx) => (
              <tr key={r[keyField] || idx} className={`border-t border-slate-100 hover:bg-emerald-50/40 ${onRowClick ? "cursor-pointer" : ""}`} onClick={() => onRowClick?.(r)} data-testid={`row-${r[keyField] || idx}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-slate-800 ${c.align === "right" ? "text-right" : ""} ${c.className || ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? "-")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (empty || <EmptyState desc="Tidak ada data untuk ditampilkan." />)}
      </div>
    </div>
  );
}
